/**
 * League rules (demo-ready PR 8): how the standings are scored and ranked,
 * the set format results must follow, and the courts. Everyone who can see
 * the league reads them; the host edits them while the league is running.
 *   GET /leagues/:id/rules
 *   PUT /leagues/:id/rules  { standings, match?, courts, recalculate? }
 * Changing how standings are scored once results are in asks the host to
 * confirm a recalculation (the server answers 409 with requires_recalculate).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../services/api/apiService';

export const TIEBREAK_LABELS = {
  h2h: 'Head-to-head',
  set_ratio: 'Set ratio',
  point_ratio: 'Point ratio',
  point_differential: 'Point difference',
  points_for: 'Points for',
  wins: 'Wins',
  league_points: 'League points',
  win_percentage: 'Win %',
};

const errorText = (error) => error?.response?.data?.message || error?.message || 'Something went wrong';

const pointsText = (s) =>
  `Win ${s.points.win}${s.draws_allowed ? ` · Draw ${s.points.draw}` : ''} · Loss ${s.points.loss} · Forfeit ${s.points.forfeit_loss}` +
  (s.deciding_set_split ? ' (a deciding-set result splits 2 / 1)' : '');

const setFormatText = (m) => {
  if (!m?.total_games) return 'Sport default';
  const sets = m.total_games === 1 ? '1 set' : `Best of ${m.total_games} sets`;
  const to = m.points_to_win ? ` to ${m.points_to_win}` : '';
  const by = m.win_by_margin === 2 ? ', win by 2' : '';
  const cap = m.max_points ? `, cap ${m.max_points}` : '';
  return `${sets}${to}${by}${cap}${m.strict_set_scores ? ' (sets must be finished)' : ''}`;
};

const toForm = (rules) => ({
  rank_by: rules.standings.rank_by,
  win: rules.standings.points.win,
  draw: rules.standings.points.draw,
  loss: rules.standings.points.loss,
  forfeit_loss: rules.standings.points.forfeit_loss,
  draws_allowed: rules.standings.draws_allowed,
  tiebreakers: rules.standings.tiebreakers,
  total_games: rules.match?.total_games ?? '',
  points_to_win: rules.match?.points_to_win ?? '',
  win_by_margin: rules.match?.win_by_margin ?? 2,
  max_points: rules.match?.max_points ?? '',
  strict_set_scores: Boolean(rules.match?.strict_set_scores),
  courts: rules.courts,
});

const RulesForm = ({ league, rules, onSaved, onCancel }) => {
  const [form, setForm] = useState(() => toForm(rules));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [needsRecalc, setNeedsRecalc] = useState(false);
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const options = rules.tiebreak_options.filter(
    (t) => t !== (form.rank_by === 'wins' ? 'wins' : 'league_points') && (rules.uses_sets || t !== 'set_ratio')
  );
  const unused = options.filter((t) => !form.tiebreakers.includes(t));

  const move = (i, delta) => {
    const list = [...form.tiebreakers];
    const j = i + delta;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    set('tiebreakers', list);
  };

  const save = async (recalculate = false) => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        standings: {
          rank_by: form.rank_by,
          points: {
            win: Number(form.win), draw: Number(form.draw), loss: Number(form.loss), forfeit_loss: Number(form.forfeit_loss),
          },
          ...(rules.uses_sets ? {} : { draws_allowed: Boolean(form.draws_allowed) }),
          tiebreakers: form.tiebreakers.filter((t) => options.includes(t)),
        },
        courts: form.courts.map((c) => c.trim()),
        ...(rules.uses_sets && form.total_games
          ? {
            match: {
              total_games: Number(form.total_games),
              ...(form.points_to_win !== '' ? { points_to_win: Number(form.points_to_win) } : {}),
              win_by_margin: Number(form.win_by_margin),
              max_points: form.max_points === '' ? null : Number(form.max_points),
              strict_set_scores: Boolean(form.strict_set_scores),
            },
          }
          : {}),
        ...(recalculate ? { recalculate: true } : {}),
      };
      const res = await apiClient.put(`/leagues/${league.id}/rules`, body);
      onSaved(res.data?.data, res.data?.message);
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data?.details?.requires_recalculate) {
        setNeedsRecalc(true);
      }
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="league-game-form league-rules-form"
      aria-label="Edit league rules"
      onSubmit={(e) => { e.preventDefault(); save(false); }}
    >
      <h5 className="league-game-form__title">Edit league rules</h5>

      <fieldset className="league-rules-form__group">
        <legend>Standings</legend>
        <div className="league-game-form__grid">
          <label>Rank by
            <select value={form.rank_by} onChange={(e) => set('rank_by', e.target.value)}>
              <option value="points">League points</option>
              <option value="wins">Wins</option>
            </select>
          </label>
          {['win', ...(form.draws_allowed && !rules.uses_sets ? ['draw'] : []), 'loss', 'forfeit_loss'].map((key) => (
            <label key={key}>{key === 'forfeit_loss' ? 'Points per forfeit' : `Points per ${key}`}
              <input type="number" min={-10} max={10} step={1} value={form[key]} onChange={(e) => set(key, e.target.value)} />
            </label>
          ))}
          {!rules.uses_sets && (
            <label className="league-rules-form__check">
              <input type="checkbox" checked={Boolean(form.draws_allowed)} onChange={(e) => set('draws_allowed', e.target.checked)} />
              Draws allowed
            </label>
          )}
        </div>
        <p className="league-rules-form__label">Tiebreaks, in order</p>
        <ol className="league-rules-form__list">
          {form.tiebreakers.filter((t) => options.includes(t)).map((t, i, list) => (
            <li key={t}>
              <span>{TIEBREAK_LABELS[t] || t}</span>
              <button type="button" aria-label={`Move ${TIEBREAK_LABELS[t]} up`} disabled={i === 0} onClick={() => move(form.tiebreakers.indexOf(t), -1)}>↑</button>
              <button type="button" aria-label={`Move ${TIEBREAK_LABELS[t]} down`} disabled={i === list.length - 1} onClick={() => move(form.tiebreakers.indexOf(t), 1)}>↓</button>
              <button type="button" aria-label={`Remove ${TIEBREAK_LABELS[t]}`} onClick={() => set('tiebreakers', form.tiebreakers.filter((x) => x !== t))}>✕</button>
            </li>
          ))}
        </ol>
        {unused.length > 0 && (
          <label className="league-rules-form__add">Add a tiebreak
            <select value="" onChange={(e) => e.target.value && set('tiebreakers', [...form.tiebreakers, e.target.value])}>
              <option value="">Choose…</option>
              {unused.map((t) => <option key={t} value={t}>{TIEBREAK_LABELS[t] || t}</option>)}
            </select>
          </label>
        )}
      </fieldset>

      {rules.uses_sets && (
        <fieldset className="league-rules-form__group">
          <legend>Sets</legend>
          <div className="league-game-form__grid">
            <label>Sets per match
              <select value={form.total_games} onChange={(e) => set('total_games', e.target.value)}>
                <option value="">Sport default</option>
                {[1, 3, 5, 7].map((n) => <option key={n} value={n}>{n === 1 ? '1 set' : `Best of ${n}`}</option>)}
              </select>
            </label>
            <label>Points per set
              <input type="number" min={1} max={99} value={form.points_to_win} onChange={(e) => set('points_to_win', e.target.value)} />
            </label>
            <label>Win by
              <select value={form.win_by_margin} onChange={(e) => set('win_by_margin', e.target.value)}>
                <option value={2}>2 points</option>
                <option value={1}>1 point</option>
              </select>
            </label>
            <label>Point cap
              <input type="number" min={1} max={199} placeholder="None" value={form.max_points} onChange={(e) => set('max_points', e.target.value)} />
            </label>
            <label className="league-rules-form__check">
              <input type="checkbox" checked={form.strict_set_scores} onChange={(e) => set('strict_set_scores', e.target.checked)} />
              Sets must reach the target (no games ended on time)
            </label>
          </div>
        </fieldset>
      )}

      <fieldset className="league-rules-form__group">
        <legend>Courts</legend>
        <ol className="league-rules-form__list">
          {form.courts.map((name, i) => (
            <li key={i}>
              <input
                aria-label={`Court ${i + 1} name`}
                value={name}
                maxLength={40}
                onChange={(e) => set('courts', form.courts.map((c, j) => (j === i ? e.target.value : c)))}
              />
              <button
                type="button"
                aria-label={`Remove court ${i + 1}`}
                disabled={form.courts.length === 1}
                onClick={() => set('courts', form.courts.filter((_, j) => j !== i))}
              >✕</button>
            </li>
          ))}
        </ol>
        <button type="button" className="league-rules-form__more" onClick={() => set('courts', [...form.courts, `Court ${form.courts.length + 1}`])}>
          + Add a court
        </button>
      </fieldset>

      {error && <p className="league-game-form__error" role="alert">{error}</p>}
      <div className="league-game-form__actions">
        {needsRecalc ? (
          <button type="button" className="league-game-form__submit" disabled={saving} onClick={() => save(true)}>
            {saving ? 'Saving…' : 'Recalculate standings and save'}
          </button>
        ) : (
          <button type="submit" className="league-game-form__submit" disabled={saving}>{saving ? 'Saving…' : 'Save rules'}</button>
        )}
        <button type="button" className="league-game-form__cancel" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </form>
  );
};

const LeagueRulesCard = ({ league, onSaved }) => {
  const [rules, setRules] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    if (!league?.id) return;
    // Wrapped so a missing or failed request leaves the rest of the page alone
    Promise.resolve()
      .then(() => apiClient.get(`/leagues/${league.id}/rules`))
      .then((res) => { setRules(res?.data?.data || null); setError(null); })
      .catch((err) => setError(errorText(err)));
  }, [league?.id]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="league-team-page__note" role="alert">{error}</p>;
  if (!rules) return null;
  const s = rules.standings;

  return (
    <section className="league-rules-card" aria-label="League rules">
      <div className="league-rules-card__head">
        <h3 className="form-section-title">⚖️ League rules</h3>
        {rules.can_edit && !editing && (
          <button type="button" className="league-rules-card__edit" onClick={() => { setEditing(true); setNotice(null); }}>
            Edit rules
          </button>
        )}
      </div>
      {notice && <p className="league-rules-card__notice" role="status">{notice}</p>}
      {editing ? (
        <RulesForm
          league={league}
          rules={rules}
          onCancel={() => setEditing(false)}
          onSaved={(data, message) => {
            setRules(data);
            setEditing(false);
            setNotice(message || 'Rules saved');
            onSaved?.(data);
          }}
        />
      ) : (
        <dl className="league-rules-card__list">
          <dt>Ranked by</dt>
          <dd>{s.rank_by === 'wins' ? 'Wins' : 'League points'}</dd>
          <dt>Points</dt>
          <dd>{pointsText(s)}</dd>
          <dt>Tiebreaks</dt>
          <dd>{s.tiebreakers.length ? s.tiebreakers.map((t) => TIEBREAK_LABELS[t] || t).join(' → ') : 'Team name'}</dd>
          {!rules.uses_sets && (<><dt>Draws</dt><dd>{s.draws_allowed ? 'Allowed' : 'Not allowed'}</dd></>)}
          {rules.uses_sets && (<><dt>Sets</dt><dd>{setFormatText(rules.match)}</dd></>)}
          <dt>Courts</dt>
          <dd>{rules.courts.join(', ')}</dd>
        </dl>
      )}
    </section>
  );
};

export default LeagueRulesCard;
